import { createSlice, current } from '@reduxjs/toolkit';
import { cloneItem, getMarkerLayer } from 'app/core/markers/models';
import { editorSlice } from 'app/stores/editor';
import { createPage, getPageKeyForMarkerAlias, getPageKeyFromMarkerLayer, } from 'app/stores/project/page';
import { initialColor, initialSize } from 'app/stores/styles';
import { patchMarker, snapPositionals } from 'app/utils/markers';
import { HistoryActions } from './history/actions';
import { pageItemsReorderReducer } from './project/reorderItems';
const initialState = {
    page: {},
    pages: [],
    images: {},
    markers: {},
    activePage: null,
    selection: [],
    size: initialSize,
    color: initialColor,
};
let copyBuffer = [];
export const projectSlice = createSlice({
    name: 'project',
    initialState,
    reducers: {
        removeProject: state => (Object.assign(Object.assign({}, initialState), { size: state.size, color: state.color })),
        setSize: (state, action) => {
            state.size = action.payload.size;
        },
        setColor: (state, action) => {
            state.color = action.payload.color;
        },
        setHover: (state, action) => {
            state.hovered = action.payload.id;
            return state;
        },
        clearHover: state => {
            delete state.hovered;
            return state;
        },
        itemSelect: (state, action) => {
            const { id } = action.payload;
            if (!id || state.selection.indexOf(id) > -1) {
                return state;
            }
            state.selection = [id];
            return state;
        },
        addImage: (state, action) => {
            const { image } = action.payload;
            if (!image) {
                return state;
            }
            state.images[image.id] = image;
            return state;
        },
        addPage: (state, action) => {
            const { page } = action.payload;
            // validate image?
            if (!page || !page.image) {
                return state;
            }
            if (!state.images[page.image]) {
                throw new Error('Missing image');
            }
            const pageId = page.id;
            state.page[pageId] = page;
            state.pages.push(pageId);
            return state;
        },
        increasePadding: (state, action) => {
            var _a, _b, _c;
            const page = state.page[(_a = action.payload.pageId) !== null && _a !== void 0 ? _a : state.activePage];
            if (!page) {
                return state;
            }
            page.style = Object.assign(Object.assign({}, page.style), { padding: ((_c = (_b = page.style) === null || _b === void 0 ? void 0 : _b.padding) !== null && _c !== void 0 ? _c : 0) * 6 });
            return state;
        },
        setPadding: (state, action) => {
            var _a;
            const page = state.page[(_a = action.payload.pageId) !== null && _a !== void 0 ? _a : state.activePage];
            if (!page) {
                return state;
            }
            page.style = Object.assign(Object.assign({}, page.style), { padding: Math.max(0, action.payload.padding) });
            return state;
        },
        setCornerRadius: (state, action) => {
            var _a;
            const page = state.page[(_a = action.payload.pageId) !== null && _a !== void 0 ? _a : state.activePage];
            if (!page) {
                return state;
            }
            page.style = Object.assign(Object.assign({}, page.style), { corner: Math.max(0, action.payload.radius) });
            return state;
        },
        setShadow: (state, action) => {
            var _a;
            const page = state.page[(_a = action.payload.pageId) !== null && _a !== void 0 ? _a : state.activePage];
            if (!page) {
                return state;
            }
            page.style = Object.assign(Object.assign({}, page.style), { shadow: Math.max(0, action.payload.shadow) });
            return state;
        },
        setBackground: (state, action) => {
            var _a;
            const page = state.page[(_a = action.payload.pageId) !== null && _a !== void 0 ? _a : state.activePage];
            if (!page) {
                return state;
            }
            page.style = Object.assign(Object.assign({}, page.style), { background: action.payload.background });
            return state;
        },
        duplicatePage: (state, action) => {
            const { id: pageId, keepItems = false } = action.payload;
            const page = state.page[pageId !== null && pageId !== void 0 ? pageId : state.activePage];
            const cloneItems = (arr) => arr
                ? arr
                    .map(id => state.markers[id])
                    .filter(item => !!item)
                    .map(item => {
                    const cloned = cloneItem(item);
                    state.markers[cloned.id] = cloned;
                    return cloned.id;
                })
                : undefined;
            // deep clone blurs
            const blurs = cloneItems(page.blurs);
            // create new page
            const newPage = createPage({
                image: page.image,
                crop: page.crop,
                blurs,
            });
            if (page.style) {
                newPage.style = JSON.parse(JSON.stringify(page.style));
            }
            if (keepItems) {
                newPage.spots = cloneItems(page.spots);
                newPage.marks = cloneItems(page.marks);
                newPage.texts = cloneItems(page.texts);
                newPage.pins = cloneItems(page.pins);
            }
            const newPageId = newPage.id;
            state.page[newPageId] = newPage;
            state.pages.push(newPageId);
            state.activePage = newPageId;
            state.selection = [];
            return state;
        },
        removeSlide: (state, action) => {
            const { id: pageId } = action.payload;
            const page = state.page[pageId !== null && pageId !== void 0 ? pageId : state.activePage];
            if (page) {
                const deleteItems = (arr) => arr
                    ? arr.forEach(id => delete state.markers[id])
                    : undefined;
                deleteItems(page.blurs);
                deleteItems(page.spots);
                deleteItems(page.marks);
                deleteItems(page.pins);
                deleteItems(page.texts);
                // (images?) remove all images used by ImageMarkers
                // (image?)
                // check if image is in use
                // delete it if not (?)
            }
            // new page
            const pageIndex = state.pages.findIndex(item => item === page.id);
            const newPageIndex = pageIndex === state.pages.length - 1
                ? pageIndex - 1
                : pageIndex;
            delete state.page[pageId];
            state.pages.splice(pageIndex, 1);
            state.activePage = state.pages[newPageIndex];
            state.selection = [];
            return state;
        },
        cropPage: (state, action) => {
            const page = state.page[state.activePage];
            if (!page) {
                return state;
            }
            const { crop } = action.payload;
            if (!crop) {
                delete page.crop;
            }
            else {
                page.crop = crop;
            }
            return state;
        },
        setActivePage: (state, action) => {
            const { pageId } = action.payload;
            // make sure page exists
            if (!state.page[pageId]) {
                return state;
            }
            // clear selection
            state.selection = [];
            state.activePage = pageId;
            return state;
        },
        clearSelection: state => {
            state.selection = [];
            return state;
        },
        itemInsert: (state, action) => {
            const { marker } = action.payload;
            const { activePage } = state;
            const page = state.page[activePage];
            state.markers[marker.id] = snapPositionals(marker);
            const insertKey = getPageKeyForMarkerAlias(marker.alias);
            page[insertKey] = page[insertKey]
                .filter(id => id !== marker.id)
                .concat([marker.id]);
            return state;
        },
        itemUpdate: (state, action) => {
            const { id, patch } = action.payload;
            const { markers } = state;
            if (id in markers) {
                // NOTE: resolve nested merge (aka deep merge) for changing props on deeper objects (eq. updating text background properties)
                markers[id] = patchMarker(markers[id], patch);
            }
            return state;
        },
        itemsUpdate: (state, action) => {
            const { patches } = action.payload;
            const { markers } = state;
            Object.entries(patches).forEach(([id, patch]) => {
                if (id in markers) {
                    markers[id] = patchMarker(markers[id], patch);
                }
            });
            return state;
        },
        itemDelete: (state, action) => {
            const { ids } = action.payload;
            const { markers } = state;
            ids.forEach(id => {
                const marker = markers[id];
                if (marker) {
                    delete markers[id];
                    const alias = marker.alias;
                    const itemSetKey = getPageKeyForMarkerAlias(alias);
                    if (itemSetKey) {
                        state.pages.forEach(pageId => {
                            const page = state.page[pageId];
                            const markerIndex = page[itemSetKey].indexOf(id);
                            if (markerIndex > -1) {
                                page[itemSetKey] = page[itemSetKey].filter(item => item !== id);
                            }
                        });
                    }
                }
                state.selection = state.selection.filter(selectedId => selectedId !== id);
                if (state.hovered === id) {
                    delete state.hovered;
                }
            });
            return state;
        },
        itemReorder: (state, action) => {
            if (!state.selection.length) {
                return state;
            }
            const selectedMarkers = state.selection.map(id => state.markers[id]);
            // make sure all are at the same layer
            const layers = selectedMarkers.map(marker => getMarkerLayer(marker.alias));
            if (!layers.every(alias => alias === layers[0])) {
                return state;
            }
            const collectionKey = getPageKeyFromMarkerLayer(layers[0]);
            // make sure all items are on same page
            const pages = selectedMarkers.map(marker => state.pages.find(pageId => {
                var _a;
                return ((_a = state.page[pageId][collectionKey]) === null || _a === void 0 ? void 0 : _a.includes(marker.id)) === true;
            }));
            if (!pages.every(page => page === pages[0])) {
                return state;
            }
            const page = state.page[pages[0]];
            // reorder
            const { direction } = action.payload;
            const newCollection = pageItemsReorderReducer(page[collectionKey].map(id => state.markers[id]), selectedMarkers, direction);
            if (newCollection) {
                page[collectionKey] = newCollection;
            }
            return state;
        },
        renumeratePins: (state, action) => {
            var _a, _b;
            const pageId = (_a = action.payload.pageId) !== null && _a !== void 0 ? _a : state.activePage;
            const initial = Math.max(1, (_b = Number(action.payload.initial)) !== null && _b !== void 0 ? _b : 1);
            const page = state.page[pageId];
            return page.pins.forEach((pinId, index) => {
                state.markers[pinId].number = index + initial;
            });
        },
        copy: state => {
            const hasSelection = state.selection.length;
            if (hasSelection) {
                copyBuffer = state.selection
                    .map(id => state.markers[id])
                    .filter(item => !!item)
                    .map(marker => cloneItem(current(marker)));
            }
        },
        paste: state => {
            if (!copyBuffer || !copyBuffer.length) {
                return;
            }
            const page = state.page[state.activePage];
            if (!page) {
                return;
            }
            let newSelection = [];
            copyBuffer.forEach((item, index) => {
                const marker = cloneItem(item);
                marker.x += 10 * (index + 1);
                marker.y += 10 * (index + 1);
                state.markers[marker.id] = snapPositionals(marker);
                const insertKey = getPageKeyForMarkerAlias(marker.alias);
                page[insertKey] = page[insertKey]
                    .filter(id => id !== marker.id)
                    .concat([marker.id]);
                newSelection = [marker.id];
            });
            state.selection = newSelection;
        },
    },
    extraReducers: (builder) => {
        builder.addCase(editorSlice.actions.setColorsPalette, (state, action) => {
            var _a;
            const { colors: palette } = action.payload;
            if (palette.indexOf(state.color) === -1) {
                state.color = (_a = action.payload.newColor) !== null && _a !== void 0 ? _a : palette[0];
            }
            return state;
        });
        builder.addMatcher((action) => action.type === HistoryActions.undo.name ||
            action.type === HistoryActions.redo.name, state => {
            delete state.hovered;
        });
    },
});
export const getProjectState = (state) => state.project.present;
